import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CONTENT_MAX_WIDE, CONTENT_INSET } from '../styles/layout';
import { BOOKING_PACKAGES } from '../constants/booking';
import {
  ChevronDown,
  Tent,
  Monitor,
  Layers,
  Radar,
  Wrench,
  Target,
  Grid3x3,
  Crosshair,
  CircleDot,
  TrendingUp,
  RotateCw,
  MessageCircle,
  Wind,
  Zap,
  Spade,
  Check,
} from 'lucide-react';

const GOLD = '#C9A84C';
const BG = '#090909';
const TEXT_MUTED = '#F5F0E8';
const includedItems = [
  {
    icon: Tent,
    title: 'Inflatable Golf Simulator',
    description: 'Premium enclosed bay for an immersive, weather-ready experience at any venue.',
  },
  {
    icon: Monitor,
    title: '4K Ultra-HD Projector',
    description: 'Crystal-clear visuals on the screen for realistic course play and competitions.',
  },
  {
    icon: Layers,
    title: 'Premium Hitting Mat & Turf',
    description: 'Tour-quality surface so every swing feels authentic and comfortable.',
  },
  {
    icon: Radar,
    title: 'Professional Launch Monitor',
    description: 'Accurate ball flight, distance, and shot data for serious and casual players alike.',
  },
  {
    icon: Wrench,
    title: 'Full Setup & Breakdown',
    description: 'Our crew handles delivery, installation, and teardown—you just show up and play.',
  },
];

const formatUsd = (amount) => `$${amount.toLocaleString()}`;

const ExperiencePackageCard = ({ pkg }) => {
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const featured = !!pkg.popular;

  return (
    <article
      className={`dd-experience-package-card${featured ? ' dd-experience-package-card--featured' : ''}`}
    >
      <div className="dd-experience-package-top">
        {featured && pkg.popularLabel ? (
          <span className="dd-experience-package-badge">{pkg.popularLabel}</span>
        ) : (
          <span className="dd-experience-package-badge dd-experience-package-badge--spacer" aria-hidden="true">
            {'\u00a0'}
          </span>
        )}
        <p className="dd-experience-package-label">{pkg.name}</p>
        <h3 className="dd-experience-package-hook">{pkg.hook}</h3>
      </div>

      <div className="dd-experience-package-price-block">
        <div className="dd-experience-package-price-row">
          <span className="dd-experience-package-price">{formatUsd(pkg.basePrice)}</span>
          <span className="dd-experience-package-duration">{pkg.priceDescriptor}</span>
        </div>
        <button
          type="button"
          className="dd-experience-package-breakdown-toggle"
          onClick={() => setBreakdownOpen((open) => !open)}
          aria-expanded={breakdownOpen}
        >
          View pricing breakdown
          <ChevronDown
            size={16}
            className={`dd-how-step-chevron${breakdownOpen ? ' dd-how-step-chevron-open' : ''}`}
            aria-hidden="true"
          />
        </button>
        {breakdownOpen && (
          <ul className="dd-experience-package-breakdown">
            {pkg.breakdown.map((item) => (
              <li key={item.label}>
                <span>{item.label}</span>
                <span>{formatUsd(item.amount)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="dd-experience-package-description">{pkg.description}</p>

      <div className="dd-experience-package-divider" />

      <div className="dd-experience-package-included">
        <p className="dd-experience-package-kicker">What&apos;s Included</p>
        <ul>
          {pkg.features.map((feature) => (
            <li key={feature}>
              <Check size={16} aria-hidden="true" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="dd-experience-package-footer">
        <Link to={`/booking?package=${pkg.id}`} className="dd-experience-package-cta">
          Book This Experience
        </Link>
        <p className="dd-experience-package-tag">{pkg.tag}</p>
      </div>
    </article>
  );
};

const gamePlanGames = [
  {
    icon: Grid3x3,
    title: 'Tic Tac Toe',
    description:
      'Land your shots in the right zones to claim squares on the grid. Classic strategy meets golf precision, three in a row wins.',
  },
  {
    icon: Crosshair,
    title: 'Darts',
    description:
      'Aim for scoring zones on a virtual dartboard using real swings. Precision and consistency separate the winners from the rest.',
  },
  {
    icon: CircleDot,
    title: 'Cup Pong',
    description:
      'Knock out targets set up like pong cups using your shots. A social, easygoing game perfect for mixed skill levels.',
  },
  {
    icon: TrendingUp,
    title: 'Ladder (30yds-180yds)',
    description:
      'Climb the ladder by landing shots at increasing distances, from 30 to 180 yards. Tests range control across every club in the bag.',
  },
  {
    icon: RotateCw,
    title: 'Wheel Spin (Club Wheel, Challenge Wheel)',
    description:
      "Spin to find out which club you're using or what challenge you're facing next. Adds an element of surprise and keeps every round unpredictable.",
  },
  {
    icon: MessageCircle,
    title: 'Call Your Bluff',
    description:
      'Call your shot before you take it, then back it up. Confidence and accuracy both get put to the test.',
  },
  {
    icon: Target,
    title: 'Closest to the Pin',
    description:
      'The classic golf challenge: whoever lands nearest the pin takes the win. Simple, competitive, and always a crowd favorite.',
  },
  {
    icon: Wind,
    title: 'Long Drive',
    description:
      'Pure distance, pure power. Whoever hits it the farthest wins bragging rights for the night.',
  },
  {
    icon: Zap,
    title: 'Knockout',
    description:
      'A round of elimination where the lowest scorer on each hole is out. Last player standing takes the title.',
  },
  {
    icon: Spade,
    title: 'Digital Divot Dare Cards™',
    description:
      'Our proprietary card game built exclusively for simulator play. Draw a card, take on the dare, and let chaos, skill, and a little luck decide who comes out on top.',
  },
];

const Packages = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [openGamePlan, setOpenGamePlan] = useState(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleGamePlan = (title) => {
    setOpenGamePlan((prev) => (prev === title ? null : title));
  };

  const containerPad = `0 ${CONTENT_INSET}`;

  const sectionHeadingStyle = {
    fontSize: isMobile ? '1.75rem' : '2.25rem',
    fontWeight: '800',
    color: GOLD,
    margin: 0,
  };

  const renderGridAccordion = (
    items,
    openKey,
    onToggle,
    idPrefix,
  ) => {
    const gridClass = [
      'dd-experience-grid',
      isMobile ? 'dd-experience-grid--stacked' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
    <div className={gridClass}>
      {items.map((item, index) => {
        const isOpen = openKey === item.title;
        const Icon = item.icon;

        return (
          <div key={item.title} className="dd-experience-grid-cell">
            <Icon className="dd-experience-grid-bg-icon" aria-hidden="true" strokeWidth={1.5} />
            <div className="dd-experience-grid-cell-inner">
              <button
                type="button"
                className="dd-experience-grid-trigger"
                onClick={() => onToggle(item.title)}
                aria-expanded={isOpen}
                aria-controls={`${idPrefix}-panel-${index}`}
                id={`${idPrefix}-trigger-${index}`}
              >
                <Icon
                  className="dd-experience-grid-icon"
                  size={isMobile ? 20 : 22}
                  color={GOLD}
                  strokeWidth={2}
                  aria-hidden="true"
                />
                <h3 className="dd-experience-grid-title">{item.title}</h3>
                <ChevronDown
                  size={isMobile ? 20 : 22}
                  className={`dd-how-step-chevron${isOpen ? ' dd-how-step-chevron-open' : ''}`}
                  aria-hidden="true"
                />
              </button>
              <div
                id={`${idPrefix}-panel-${index}`}
                role="region"
                aria-labelledby={`${idPrefix}-trigger-${index}`}
                className={`dd-how-step-panel${isOpen ? ' dd-how-step-panel-open' : ''}`}
              >
                <div className="dd-how-step-panel-inner">
                  <p className="dd-how-step-description dd-experience-grid-description">
                    {item.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
    );
  };

  return (
    <div className="dd-page dd-packages" style={{ backgroundColor: BG }}>
      <Helmet>
        <title>The Experience | Digital Divot Golf — Mobile Simulator Packages</title>
        <meta
          name="description"
          content="Explore Digital Divot's premium mobile golf simulator experience — Trackman technology, lounge areas, and photo packages for Phoenix & Scottsdale events."
        />
        <meta property="og:title" content="The Experience | Digital Divot Golf" />
        <meta property="og:description" content="Premium mobile golf simulator packages for events in Arizona." />
        <meta property="og:image" content="https://digitaldivotgolf.com/TheUpdatedExperienceDobson.png" />
        <meta property="og:url" content="https://digitaldivotgolf.com/packages" />
      </Helmet>
      {/* Hero + What's Included overlay */}
      <section style={{ backgroundColor: BG, color: '#F5F0E8' }}>
        <div className="dd-experience-hero">
          <img
            className="dd-experience-hero-img"
            src="/TheUpdatedExperienceDobson.png"
            alt="Digital Divot premium mobile golf simulator setup"
            width={1920}
            height={1080}
            loading="eager"
          />
          <div className="dd-experience-hero-scrim" aria-hidden="true" />
          <div className="dd-experience-hero-bottom">
            <div className="dd-experience-hero-copy">
              <span
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  letterSpacing: '0.2em',
                  color: GOLD,
                  marginBottom: '12px',
                }}
              >
                THE EXPERIENCE
              </span>
              <h2
                style={{
                  fontSize: isMobile ? '1.75rem' : '2.5rem',
                  fontWeight: '800',
                  color: '#F5F0E8',
                  lineHeight: 1.2,
                  margin: '0 0 12px',
                }}
              >
                Your Golf Course. Any Venue.
              </h2>
              <p
                style={{
                  fontSize: isMobile ? '0.9375rem' : '1.0625rem',
                  color: TEXT_MUTED,
                  lineHeight: 1.6,
                  margin: 0,
                  maxWidth: '32rem',
                }}
              >
                Delivered, set up, and ready to play at your venue.
              </p>
            </div>

            <div className="dd-experience-included-overlay">
            <div
              className="dd-experience-included-inner"
              style={{
                maxWidth: CONTENT_MAX_WIDE,
                margin: '0 auto',
                padding: containerPad,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ textAlign: 'center', marginBottom: isMobile ? '8px' : '20px' }}>
                <h2
                  style={{
                    fontSize: isMobile ? '1.15rem' : '2.25rem',
                    fontWeight: '800',
                    color: GOLD,
                    marginBottom: isMobile ? '4px' : '12px',
                  }}
                >
                  What&apos;s Included
                </h2>
                {!isMobile && (
                  <p style={{ color: TEXT_MUTED, fontSize: '1.0625rem', margin: 0 }}>
                    Every booking comes fully equipped — no hidden extras.
                  </p>
                )}
              </div>

              <div
                className="dd-included-strip"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: isMobile ? '0.65rem 0.5rem' : 'clamp(2rem, 5vw, 4rem)',
                }}
              >
                {includedItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        maxWidth: isMobile ? '5.5rem' : '10rem',
                      }}
                    >
                      <Icon
                        size={isMobile ? 20 : 28}
                        color={GOLD}
                        style={{ marginBottom: isMobile ? '4px' : '10px' }}
                      />
                      <span
                        style={{
                          fontSize: isMobile ? '0.6875rem' : '0.875rem',
                          fontWeight: '600',
                          color: '#F5F0E8',
                          lineHeight: 1.25,
                        }}
                      >
                        {item.title}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* Build Your Game Plan */}
      <section
        style={{
          padding: isMobile
            ? '1.25rem 1rem 2.5rem'
            : 'clamp(1.25rem, 2vw, 1.75rem) clamp(1rem, 4vw, 2rem) clamp(2.5rem, 5vw, 3.5rem)',
          backgroundColor: BG,
          overflow: 'hidden',
        }}
      >
        <div style={{ maxWidth: CONTENT_MAX_WIDE, margin: '0 auto', padding: containerPad, width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '2rem' : '3rem' }}>
            <h2 style={sectionHeadingStyle}>Build Your Game Plan</h2>
          </div>

          {renderGridAccordion(gamePlanGames, openGamePlan, toggleGamePlan, 'game-plan')}
        </div>
      </section>

      {/* Experience formats */}
      <section
        className="dd-addons-section dd-experience-packages-section"
        style={{
          padding: isMobile
            ? '2rem 1rem 2.5rem'
            : 'clamp(2.5rem, 5vw, 3.5rem) clamp(1rem, 4vw, 2rem) clamp(3rem, 6vw, 4rem)',
          backgroundColor: BG,
          overflow: 'hidden',
        }}
      >
        <div style={{ maxWidth: CONTENT_MAX_WIDE, margin: '0 auto', padding: containerPad, width: '100%' }}>
          <div className="dd-experience-packages-intro">
            <h2 className="dd-addons-heading">Choose Your Experience</h2>
            <p className="dd-experience-packages-subhead">One premium mobile golf setup. Three ways to play.</p>
            <p className="dd-addons-subheading">
              Every activation includes a four-hour experience, full delivery and setup, premium
              simulator equipment, and an on-site Digital Divot host. Choose the format that fits
              your event.
            </p>
          </div>

          <div className="dd-experience-packages">
            {BOOKING_PACKAGES.map((pkg) => (
              <ExperiencePackageCard key={pkg.id} pkg={pkg} />
            ))}
          </div>

          <div className="dd-experience-payment-strip">
            <h3>Booking &amp; Payment</h3>
            <p>
              A 25% deposit secures your event date. Competitive Play and Skills Trainer include
              additional experience costs as shown above.
            </p>
          </div>

          <div className="dd-experience-packages-help">
            <p>Not sure which experience fits your event?</p>
            <a
              href="mailto:events@digitaldivotgolf.com?subject=Event%20consultation"
              className="dd-experience-packages-help-btn"
            >
              Talk With Our Team
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Packages;
